/**
 * Spec export controls — Preview DOCX button (BUG-049) + Export
 * dropdown with jurisdiction override (FEAT-003) + audit-only
 * export (FEAT-004).
 *
 * Preview button uses a blob-fetch path so cookies follow on
 * cross-origin BACKEND_URL deploys; loading state during the
 * fetch keeps the drafter informed during the 2-5s render.
 *
 * Export dropdown carries:
 *   - "Export as <matter jurisdiction>" (default, highlighted)
 *   - "Export as US / EP / IN" alternates (only the
 *     jurisdictions different from the matter's are listed)
 *   - "Export with audit appendix" (existing endpoint with
 *     include_provenance=true)
 *   - "Export audit report only" (FEAT-004 standalone endpoint)
 *   - "Export bundle (ZIP)" (existing — opens BundleExportModal)
 *
 * Preview dropdown chevron offers the same jurisdiction options
 * for the inline preview.
 */
import React, { useCallback, useState } from 'react';
import {
  ChevronDown, Download, Eye, FileText, Loader2,
  Archive, ShieldCheck,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  fetchSpecPreviewDocxBlob,
  downloadSpecDocx,
  downloadSpecAuditDocx,
} from '@/api/client';
import { useSpecStore } from '@/store/specStore';

type Juris = 'US' | 'EP' | 'IN';

const _JURIS_LABEL: Record<Juris, string> = {
  US: 'US (USPTO format)',
  EP: 'EP (EPO format)',
  IN: 'IN (IPO India format)',
};

interface Props {
  matterId: string;
  hasMinimumContent: boolean;
  /** Matter's locked jurisdiction. Used as the default for both
   * preview and export; the override options only show the
   * other two jurisdictions. */
  matterJurisdiction: string | null;
  onOpenBundleModal: () => void;
}

export default function SpecExportMenu({
  matterId, hasMinimumContent, matterJurisdiction, onOpenBundleModal,
}: Props): JSX.Element {
  const showToast = useSpecStore((s) => s.showToast);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [exporting, setExporting] = useState<string | null>(null);

  const matterJuris: Juris = (
    (matterJurisdiction || '').toUpperCase() === 'US'
      ? 'US'
      : (matterJurisdiction || '').toUpperCase() === 'IN'
        ? 'IN'
        : 'EP'
  );
  const otherJuris: Juris[] = (['US', 'EP', 'IN'] as Juris[])
    .filter((j) => j !== matterJuris);

  // ── Preview ────────────────────────────────────────────────────
  const openPreview = useCallback(async (
    juris?: Juris,
  ): Promise<void> => {
    if (!hasMinimumContent || previewLoading) return;
    setPreviewLoading(true);
    try {
      const blob = await fetchSpecPreviewDocxBlob(matterId, {
        includeProvenance: false,
        ...(juris ? { jurisdiction: juris } : {}),
      });
      const url = URL.createObjectURL(blob);
      // Open in a new tab. Browsers with an Office handler will
      // preview; otherwise the user gets the standard DOCX
      // open-with prompt.
      const win = window.open(url, '_blank', 'noopener,noreferrer');
      if (!win) {
        // Pop-up blocked — fall back to forcing a download via
        // a synthetic click so the drafter still gets the file.
        const a = document.createElement('a');
        a.href = url;
        a.target = '_blank';
        a.rel = 'noopener noreferrer';
        a.click();
      }
      // Revoke the blob URL once the new tab has had time to
      // pick it up. 60s is generous for slow Office handler
      // launches; the new tab caches the bytes by then.
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e: unknown) {
      const msg = (e as { message?: string })?.message
        || 'Preview failed.';
      showToast(`Preview DOCX failed: ${msg}`);
    } finally {
      setPreviewLoading(false);
    }
  }, [matterId, hasMinimumContent, previewLoading, showToast]);

  // ── Export (full DOCX, optional jurisdiction override + audit
  //    appendix toggle). Uses the existing downloadSpecDocx
  //    helper which already wires the X-Spec-Export-Warnings
  //    header through. ─────────────────────────────────────────
  const exportDocx = useCallback(async (
    opts: { juris?: Juris; includeAudit?: boolean; key: string },
  ): Promise<void> => {
    if (!hasMinimumContent || exporting) return;
    setExporting(opts.key);
    try {
      const { blob, filename } = await downloadSpecDocx(matterId, {
        includeProvenance: !!opts.includeAudit,
        ...(opts.juris ? { jurisdiction: opts.juris } : {}),
      });
      _triggerDownload(blob, filename);
      showToast(
        opts.juris && opts.juris !== matterJuris
          ? `Exported as ${opts.juris} format. Matter jurisdiction `
            + `(${matterJuris}) unchanged.`
          : 'Exported.',
      );
    } catch (e: unknown) {
      const msg = (e as { message?: string })?.message || 'Export failed.';
      showToast(`Export failed: ${msg}`);
    } finally {
      setExporting(null);
    }
  }, [matterId, hasMinimumContent, exporting, matterJuris, showToast]);

  const exportAuditOnly = useCallback(async (): Promise<void> => {
    if (exporting) return;
    setExporting('audit_only');
    try {
      const { blob, filename } = await downloadSpecAuditDocx(matterId);
      _triggerDownload(blob, filename);
      showToast('Audit report exported.');
    } catch (e: unknown) {
      const msg = (e as { message?: string })?.message || 'Audit export failed.';
      showToast(`Audit export failed: ${msg}`);
    } finally {
      setExporting(null);
    }
  }, [matterId, exporting, showToast]);

  // ── UI ─────────────────────────────────────────────────────────
  return (
    <div className="flex items-center gap-1">
      {/* Preview button (split into a primary + chevron). The
          primary fires the matter-default preview; the chevron
          opens a tiny menu for the jurisdiction override. */}
      <div className="inline-flex">
        <button
          type="button"
          className="inline-flex h-9 items-center gap-1 rounded-l-sm rounded-r-none border border-zinc-300 bg-white px-3 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:opacity-50"
          disabled={!hasMinimumContent || previewLoading}
          onClick={() => { void openPreview(); }}
          data-testid="spec-preview-docx"
          title={
            hasMinimumContent
              ? `Open a fresh DOCX preview in a new tab (${matterJuris})`
              : 'Generate at least one section first'
          }
        >
          {previewLoading
            ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            : <Eye className="h-4 w-4" aria-hidden />}
          {previewLoading ? 'Generating preview…' : 'Preview DOCX'}
        </button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="inline-flex h-9 items-center justify-center rounded-l-none rounded-r-sm border border-l-0 border-zinc-300 bg-white px-1.5 text-zinc-500 hover:bg-zinc-50 disabled:opacity-50"
              disabled={!hasMinimumContent || previewLoading}
              data-testid="spec-preview-docx-menu-trigger"
              aria-label="Preview jurisdiction options"
              title="Preview as a different jurisdiction"
            >
              <ChevronDown className="h-3 w-3" aria-hidden />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="min-w-[14rem]">
            <DropdownMenuLabel>Preview as…</DropdownMenuLabel>
            <DropdownMenuItem
              onClick={() => { void openPreview(); }}
              data-testid="spec-preview-juris-default"
            >
              <Eye className="mr-2 h-3.5 w-3.5" aria-hidden />
              Preview as {matterJuris} (default)
            </DropdownMenuItem>
            {otherJuris.map((j) => (
              <DropdownMenuItem
                key={j}
                onClick={() => { void openPreview(j); }}
                data-testid={`spec-preview-juris-${j}`}
              >
                <Eye className="mr-2 h-3.5 w-3.5" aria-hidden />
                Preview as {_JURIS_LABEL[j]}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Export dropdown (split — primary fires matter-default
          export, chevron offers overrides + audit + bundle). */}
      <div className="inline-flex">
        <button
          type="button"
          className="inline-flex h-9 items-center gap-1 rounded-l-sm rounded-r-none border border-zinc-300 bg-white px-3 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:opacity-50"
          disabled={!hasMinimumContent || !!exporting}
          onClick={() => { void exportDocx({ key: 'default' }); }}
          data-testid="spec-export"
          title={
            hasMinimumContent
              ? `Export DOCX (${matterJuris})`
              : 'Generate at least one section first'
          }
        >
          {exporting === 'default'
            ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            : <Download className="h-4 w-4" aria-hidden />}
          {exporting === 'default' ? 'Exporting…' : 'Export'}
        </button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="inline-flex h-9 items-center justify-center rounded-l-none rounded-r-sm border border-l-0 border-zinc-300 bg-white px-1.5 text-zinc-500 hover:bg-zinc-50 disabled:opacity-50"
              disabled={!hasMinimumContent || !!exporting}
              data-testid="spec-export-menu-trigger"
              aria-label="Export options"
              title="Export options"
            >
              <ChevronDown className="h-3 w-3" aria-hidden />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="min-w-[18rem]">
            <DropdownMenuLabel>Export</DropdownMenuLabel>
            <DropdownMenuItem
              onClick={() => { void exportDocx({ key: 'default' }); }}
              data-testid="spec-export-juris-default"
            >
              <Download className="mr-2 h-3.5 w-3.5" aria-hidden />
              Export as {matterJuris} (default)
            </DropdownMenuItem>
            {otherJuris.map((j) => (
              <DropdownMenuItem
                key={j}
                onClick={() => { void exportDocx({ juris: j, key: `juris_${j}` }); }}
                data-testid={`spec-export-juris-${j}`}
              >
                <Download className="mr-2 h-3.5 w-3.5" aria-hidden />
                <span className="flex flex-col">
                  <span>Export as {_JURIS_LABEL[j]}</span>
                  <span className="text-[10px] text-zinc-500">
                    Matter jurisdiction stays {matterJuris}
                  </span>
                </span>
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
            <DropdownMenuLabel>Audit</DropdownMenuLabel>
            <DropdownMenuItem
              onClick={() => {
                void exportDocx({ includeAudit: true, key: 'with_audit' });
              }}
              data-testid="spec-export-with-audit"
            >
              <ShieldCheck className="mr-2 h-3.5 w-3.5" aria-hidden />
              <span className="flex flex-col">
                <span>Export with audit appendix</span>
                <span className="text-[10px] text-zinc-500">
                  Spec body + Appendix A.1–A.10
                </span>
              </span>
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => { void exportAuditOnly(); }}
              data-testid="spec-export-audit-only"
            >
              <FileText className="mr-2 h-3.5 w-3.5" aria-hidden />
              <span className="flex flex-col">
                <span>Export audit report only</span>
                <span className="text-[10px] text-zinc-500">
                  Provenance + audit DOCX (no spec body)
                </span>
              </span>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={onOpenBundleModal}
              data-testid="spec-export-bundle"
            >
              <Archive className="mr-2 h-3.5 w-3.5" aria-hidden />
              Export bundle (ZIP)…
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}

function _triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    window.setTimeout(() => URL.revokeObjectURL(url), 30_000);
  }
}
